import DataStore from "@shared/services/DataStore";
import generateToken, { FEEDBACK_MODULE } from "../hooks/generateToken";
import FeedbackFormService from "./FeedbackFormService";

class WebViewFlowService {
    constructor() {
        this.eventHandler = null;

        // global subscribers set (prevents duplicates)
        this.subscribers = new Set();

        // track navigation context
        this.navigate = null;
        this.previousRoute = null;
    }

    subscribe(callback) {
        if (typeof callback !== "function") return;
        this.subscribers.add(callback);

        // return unsubscribe function
        return () => this.unsubscribe(callback);
    }

    unsubscribe(callback) {
        this.subscribers.delete(callback);
    }

    dispatch(data) {
        // global subscribers
        this.subscribers.forEach(cb => {
            try {
                cb(data);
            } catch (err) {
                console.error("WebView subscriber error", err);
            }
        });
    }

    async start({ module = FEEDBACK_MODULE, navigate, location, onEvent, onError }) {
        try {
            // 1. store navigate & previous route
            this.navigate = navigate;
            this.previousRoute = location?.pathname || "/";

            // 2. Generate token
            const token = await generateToken(module);
            const activeLanguage = DataStore.getData().prefs.lang;

            // 3. Route using router
            navigate(`/ibs-kiosk/app/web-view/${module?.toLowerCase()}`, {
                webViewUrl: `${window.location?.origin}/ibs-kiosk/pub/support/view-form/?token=${encodeURIComponent(
                    token
                )}&lang=${activeLanguage}`
            });

            // 4. Attach event listener
            this._attachListener(onEvent);
        } catch (err) {
            console.error("WebView flow failed", err);
            onError && onError(err);
        }
    }

    /**
     * New: FEEDBACK-specific flow for add-beneficiary / post-transaction (and any future type).
     * Performs render-check first; only navigates into the webview if render === true.
     *
     * @param {Object} params
     * @param {Function} params.navigate
     * @param {Object} params.location
     * @param {"ADD_BENEFICIARY"|"POST_TRANSACTION"} params.type
     * @param {Object} [params.payload={}]
     * @param {Function} [params.onEvent]
     * @param {Function} [params.onError]
     * @param {Function} [params.onSkip] - called when render === false
     */
    async startFeedbackForm({ navigate, location, type, payload = {}, onEvent, onError, onSkip }) {
        try {
            // 1. store navigate & previous route
            this.navigate = navigate;
            this.previousRoute = location?.pathname || "/";

            const activeLanguage = DataStore.getData().prefs.lang;

            // 2. Check and render flow
            const { shouldRender, webViewUrl, reason } = await FeedbackFormService.resolveFeedbackForm({
                type,
                payload,
                lang: activeLanguage
            });

            if (!shouldRender) {
                console.log("Feedback form skipped:", reason);
                onSkip && onSkip(reason);
                return { rendered: false, reason };
            }

            // 3. Route using router
            navigate(`/ibs-kiosk/app/web-view/${FEEDBACK_MODULE.toLowerCase()}`, {
                webViewUrl
            });

            // 4. Attach event listener
            this._attachListener(onEvent);

            return { rendered: true };
        } catch (err) {
            console.error("Feedback WebView flow failed", err);
            onError && onError(err);
            return { rendered: false, reason: "error" };
        }
    }

    _attachListener(onEvent) {
        this.eventHandler = event => {
            try {
                const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
                console.log("WebView Event:", data);
                onEvent && onEvent(data);
                this.dispatch(data);
            } catch (err) {
                console.error("Invalid WebView event:", event.data);
            }
        };
        window.addEventListener("message", this.eventHandler);
    }

    close() {
        // cleanup listeners
        this.stop();

        // clear subscribers (optional but clean)
        this.subscribers.clear();

        // navigate back
        if (this.navigate) {
            this.navigate(this.previousRoute, { replace: true });
        }
    }

    stop() {
        if (this.eventHandler) {
            window.removeEventListener("message", this.eventHandler);
            this.eventHandler = null;
        }
    }
}

export default new WebViewFlowService();
